import React, { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { AxiosResponse } from 'axios';

import Breadcrumbs from 'components/breadcrumbs';
import Collapsible from 'components/collapsible';
import DatePicker from 'components/date-picker';
import Dialog from 'components/dialog';
import Error from 'components/error';
import FilterSidebar from 'components/filter-sidebar';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Slider from 'components/slider';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { MessageSquare } from 'lucide-react';
import Mui from 'materials';

import { Cage } from 'types/shared/cage.type';
import { CreateWelfareFieldCollectionInput } from 'views/fish-check/features/welfare-scoring/types/create-welfare-field-collection-input.type';
import { FishWelfareRegistration } from 'views/fish-check/types/field-collection.type';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import _, { Dictionary } from 'underscore';

import { Alert, Box, Button, useMediaQuery, useTheme } from '@mui/material';
import FlexBox from 'components/flex-box';
import dayjs from 'dayjs';
import { getFieldCollection, getFieldCollectionCages } from 'views/fish-check/api/get-field-collection';
import FishCheckHeader from '../../../components/fish-check-header';
import FishCheckTabsHeader from '../../../components/fish-check-tabs-header';
import { postFieldCollectionWelfare } from '../api/create-field-collection';
import { deleteFieldCollectionWelfare } from '../api/delete-field-collection';
import { getReportTemplates } from '../api/get-templates';
import { putFieldCollectionWelfare } from '../api/update-field-collection';
import { CountMarks, buttons, templateFields } from '../constants/edit-welfare-scoring';
import Paper from 'components/paper';

type ParamTypes = {
  id: string;
};

type Props = {};

const EditWelfareScoring: React.FC<Props> = () => {
  const params = useParams<ParamTypes>();
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { account } = useAccountState();

  const snackbarDispatch = useSnackbarDispatch();
  const addFishDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const removeDialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);
  const updateDialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const PAGE_NAME = 'fishCheck';
  const PAGE_NAME_ID = 'fishCheck.editWelfareScoring.fishWelfareData';

  // #region STATES
  const [unit, setUnit] = useState<Cage>();

  // hacky fix to display n/a for number text fields
  const [tempWeight, setTempWeight] = useState<string>('0');
  const [tempLength, setTempLength] = useState<string>('0');
  const [isValid, setIsValid] = useState<boolean>(true);
  // #endregion STATES

  // #region FORMS

  const defaultFieldCollection = {
    fishGroupId: unit?.fishGroupId ?? '',
    cageId: unit?.id ?? '',
    dateCreated: dayjs().format('YYYY-MM-DD'),
    comment: '',
    adultFemaleLice: -1,
    wounds: -1,
    skinBleeding: -1,
    length: 0,
    weight: 0,
    kFactor: -1,
    stationaryLice: -1,
    mobileLice: -1,
    scottishLice: -1,
    backboneDeformity: -1,
    thinness: -1,
    maturation: -1,
    scaleLoss: -1,
    snoutWounds: -1,
    jawDeformity: -1,
    paleEye: -1,
    eyeDamage: -1,
    gillCover: -1,
    gillStatus: -1,
    finStatus: -1,
    firstImpression: -1,
  };

  const {
    handleSubmit: handleSubmitFieldCollection,
    control: controlFieldCollection,
    watch: watchFieldCollection,
    setValue: setValueFieldCollection,
    getValues: getValuesFieldCollection,
    reset: resetFieldCollection,
  } = useForm<CreateWelfareFieldCollectionInput>({
    defaultValues: {
      ...defaultFieldCollection,
    },
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  // #endregion FORMS

  // #region FETCH DATA
  const {
    data: fieldCollection,
    error,
    isError,
    isLoading,
  } = useGet(['getFieldCollection', params.id], (context) => getFieldCollection(Number(context.queryKey[1])), {
  });

  const { data: reportTemplates } = useGet(['getReportTemplates', account.company?.companyId], ({ queryKey }) => getReportTemplates(String(queryKey[1])), {
    enabled: !!fieldCollection, // wait until field collection is returned
  });

  const { data: cages, isLoading: cagesIsLoading } = useGet(
    ['getFieldCollectionCages', fieldCollection?.fieldRegistration.globalSiteId],
    ({ queryKey }) => getFieldCollectionCages(String(queryKey[1])),
    {
      enabled: !!fieldCollection?.fieldRegistration.globalSiteId,
    }
  );

  const welfare = useMemo<Dictionary<FishWelfareRegistration[]>>(
    () =>
      _.chain(fieldCollection?.fishWelfareRegistration ?? [])
        .sortBy((fw) => dayjs(fw.dateCreated))
        .reverse()
        .groupBy((fw) => dayjs(fw.dateCreated).format('L'))
        .value(),
    [fieldCollection]
  );

  const fields = useMemo(
    () => reportTemplates?.find((template) => template.name === fieldCollection?.fieldRegistration.name)?.fields.map((field) => field.value) || [],
    [fieldCollection, reportTemplates]
  );

  const cageMap = useMemo<{ [key: string]: string }>(
    () => cages?.reduce((acc, curr) => ({ ...acc, [curr.id]: curr.name }), {}) || {},
    [cages]
  );

  // #endregion FETCH DATA

  // #region POST DATA

  const addFieldCollection = usePost<AxiosResponse, CreateWelfareFieldCollectionInput>(
    (request) => postFieldCollectionWelfare(Number(params.id), request),
    () => {
      resetFieldCollection({
        ...defaultFieldCollection,
        dateCreated: getValuesFieldCollection('dateCreated'),
      });
      queryClient.invalidateQueries({
        queryKey: ['getFieldCollection']
      });
      addFishDialogRef.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const updateFieldCollectionRegistration = usePost<number, { registrationId: number; request: CreateWelfareFieldCollectionInput }>(
    (request) => putFieldCollectionWelfare(Number(params.id), request.registrationId, request.request),
    (response) => {
      queryClient.invalidateQueries({
        queryKey: ['getFieldCollection']
      });
      updateDialogRef.current[response].setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const deleteFieldCollection = usePost<number, number>(
    (request) => deleteFieldCollectionWelfare(Number(params.id), request),
    (response) => {
      queryClient.invalidateQueries({
        queryKey: ['getFieldCollection']
      });
      removeDialogRef.current[response].setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // #endregion POST DATA

  // #region RENDERS

  const hideCell = (rows: FishWelfareRegistration[]): { [key: string]: boolean } =>
    fields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: rows.every((r) => {
          if (typeof r[field as keyof FishWelfareRegistration] === 'number') {
            return Number(r[field as keyof FishWelfareRegistration]) < 0;
          }
          return false;
        }),
      }),
      {}
    );

  const renderCheckValue = (value: string | number): string => {
    if (String(value) === '-1') return intl.formatMessage({ id: 'general.notApplicable' });
    return String(value);
  };

  const renderCheckShow = (name: string) => {
    return fields.length <= 0 || fields.includes(name);
  };

  const renderFishCheckRow = (row: FishWelfareRegistration, hideCell: { [key: string]: boolean }, idx: number) => {
    return (
      <ListRow key={row.id}>
        <ListCell align="center">{idx + 1}</ListCell>
        {fields.map(
          (col) =>
            !hideCell[col] && (
              <ListCell key={`${row.id}_${col}`} align="right">
                {renderCheckValue(row[col as keyof FishWelfareRegistration])}
              </ListCell>
            )
        )}
        {<ListCell align="right">{cageMap[row.cageId]}</ListCell>}
        {<ListCell align="right">{cages?.find((c) => c.fishGroupId === row.fishGroupId)?.fishGroupName}</ListCell>}
        <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Box display="flex" justifyContent={{ xs: 'space-between', md: 'flex-end' }} alignItems="center" gap={2} width={'100%'}>
            <Dialog
              maxWidth="xs"
              ref={(el) => {
                if (el) return (removeDialogRef.current[idx] = el);
              }}
              title={'Comment'}
              button={
                <Mui.IconButton disabled={row.comment.length <= 0} color="primary" title="Read comment">
                  <MessageSquare size={18} />
                </Mui.IconButton>
              }>
              <div>{row.comment}</div>
            </Dialog>
            <Dialog
              ref={(el) => {
                if (el) return (removeDialogRef.current[row.id] = el);
              }}
              maxWidth="sm"
              title={intl.formatMessage({ id: `${PAGE_NAME}.navigation.dialog.deleteTitle` })}
              button={<Button size="small">{intl.formatMessage({ id: 'general.delete' })}</Button>}>
              <Box display={'flex'} flexDirection={'column'} gap={4}>
                <Box>
                  <FormattedMessage id="general.deleteComment" />
                </Box>
                <Box display={'flex'} justifyContent={'end'} gap={4}>
                  <Button variant="outlined" type="submit" size="small" onClick={() => removeDialogRef.current[row.id]?.close()}>
                    <FormattedMessage id="general.cancel" />
                  </Button>
                  <Button type="submit" onClick={() => handleDeleteFieldCollection(row.id)}>
                    <FormattedMessage id={'general.delete'} />
                  </Button>
                </Box>
              </Box>
            </Dialog>
            <Dialog
              ref={(el) => {
                if (el) return (updateDialogRef.current[row.id] = el);
              }}
              maxWidth="sm"
              title={intl.formatMessage({ id: `${PAGE_NAME}.editWelfareScoring.editTitle` })}
              button={
                <Button
                  onClick={() => {
                    resetFieldCollection({
                      ...row,
                    });
                    setTempLength(row.length === 0 ? 'N/A' : String(row.length));
                    setTempWeight(row.weight === 0 ? 'N/A' : String(row.weight));
                    setIsValid(true);
                  }}
                  size="small">
                  {intl.formatMessage({ id: 'general.edit' }, { type: '' })}
                </Button>
              }>
              <form onSubmit={handleSubmitFieldCollection((data) => handleUpdateFieldCollectionRegistration(row.id, data))}>
                {updateFieldCollectionRegistration.isPending && <LoadingSpinner overlay />}
                <FlexBox>
                  {renderFieldCollectionForm()}
                  <Box display={'flex'} justifyContent={'end'}>
                    <Button type="submit">
                      <FormattedMessage id={`${PAGE_NAME}.editWelfareScoring.updateSample`} />
                    </Button>
                  </Box>
                </FlexBox>
              </form>
            </Dialog>
          </Box>
        </ListCell>
      </ListRow>
    );
  };

  const renderFormFields = () => {
    return templateFields
      .filter(({ name }) => fields.includes(name))
      .map(({ order, name, type }) => {
        const label = intl.formatMessage({
          id: `${PAGE_NAME_ID}.${name.charAt(0).toLocaleLowerCase() + name.slice(1)}`,
        });

        if (type === 'slider') {
          return (
            <Slider
              key={name}
              label={label}
              marks={CountMarks}
              min={-1}
              max={10}
              step={1}
              controller={{
                name,
                control: controlFieldCollection,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: validateForm,
                },
              }}
            />
          );
        }

        if (type === 'select') {
          return (
            <Select
              key={name}
              dark
              fullWidth
              label={label}
              controller={{
                name,
                control: controlFieldCollection,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: validateForm,
                },
              }}>
              {buttons.map((button) => (
                <RadioSelectItem key={button.value} value={button.value}>
                  {button.label}
                </RadioSelectItem>
              ))}
            </Select>
          );
        }
        return null;
      });
  };

  const renderFieldCollectionForm = () => {
    return (
      <>
        <Select
          dark
          fullWidth
          label={intl.formatMessage({ id: `${PAGE_NAME}.editWelfareScoring.fishGroupIdLabel` })}
          controller={{
            name: 'fishGroupId',
            control: controlFieldCollection,
            rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
          }}
          onChange={(event) => {
            const cage = cages?.find((cage) => cage.fishGroupId === String(event.target.value));
            if (cage) {
              setUnit(cage);
              setValueFieldCollection('cageId', cage.id);
            }
          }}>
          {cages
            ?.filter((cage) => cage.fishGroupId)
            .map((cage) => (
              <RadioSelectItem
                value={cage.fishGroupId}
                key={cage.fishGroupId}
                selected={cage.fishGroupId === watchFieldCollection('fishGroupId')}>
                {cage.name}
              </RadioSelectItem>
            ))}
        </Select>
        <DatePicker
          dark
          fullWidth
          label={intl.formatMessage({ id: `${PAGE_NAME}.editWelfareScoring.dateCreatedLabel` })}
          controller={{
            name: 'dateCreated',
            control: controlFieldCollection,
            rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
          }}
        />
        {renderCheckShow('length') && (
          <TextField
            dark
            fullWidth
            type="number"
            placeholder="N/A"
            value={tempLength}
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.length` })}
            endAdornment={'cm'}
            onChange={(event) => {
              setTempLength(event.target.value);
              setValueFieldCollection('length', event.target.value ? Number(event.target.value) : 0);
              validateForm();
            }}
          />
        )}
        {renderCheckShow('weight') && (
          <TextField
            dark
            fullWidth
            type="number"
            placeholder="N/A"
            value={tempWeight}
            endAdornment={'g'}
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.weight` })}
            onChange={(event) => {
              setTempWeight(event.target.value);
              setValueFieldCollection('weight', !!event.target.value ? Number(event.target.value) : 0);
              validateForm();
            }}
          />
        )}
        {renderFormFields()}
        <TextField
          fullWidth
          dark
          multiline
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.comment` })}
          controller={{
            name: 'comment',
            control: controlFieldCollection,
          }}
        />
        {!isValid && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            {intl.formatMessage({ id: `${PAGE_NAME}.editWelfareScoring.alert` })}
          </Alert>
        )}
      </>
    );
  };
  // #endregion RENDERS

  // #region HANDLERS

  const handleAddFieldCollection = (request: CreateWelfareFieldCollectionInput) => {
    updateCalculations(request);
    addFieldCollection.mutate(request);
  };

  const handleUpdateFieldCollectionRegistration = (registrationId: number, request: CreateWelfareFieldCollectionInput) => {
    updateCalculations(request);
    updateFieldCollectionRegistration.mutate({ registrationId, request });
  };

  const handleDeleteFieldCollection = (registrationId: number) => {
    deleteFieldCollection.mutate(registrationId);
  };

  // #endregion HANDLERS

  // #region HELPERS

  // check if the form has at least one field changed from default values that are not production unit (cage) or date

  // minor bug when only weight OR length are available, a form can be submitted if still n/a
  const validateForm = () => {
    let visibleFields: string[] = [];
    Object.keys(getValuesFieldCollection()).forEach((v) => {
      if (renderCheckShow(v)) visibleFields.push(v);
    });

    let validFields = visibleFields.some((v) => {
      let val = getValuesFieldCollection(v as keyof CreateWelfareFieldCollectionInput);
      if (v === 'weight' || v === 'length') return !_.isEmpty(String(val)) && Number(val) >= 0;
      else return !(String(val) === '0');
    });

    setIsValid(validFields);
    return validFields;
  };

  const updateCalculations = (request: CreateWelfareFieldCollectionInput) => {
    request.weight = Number(tempWeight) >= 0 ? Number(tempWeight) : 0;
    request.length = Number(tempLength) >= 0 ? Number(tempLength) : 0;
    const kFactorValue = !(request.length === 0 || request.weight === 0)
      ? (request.weight * 100) / (request.length * request.length * request.length)
      : -1;
    request.kFactor = kFactorValue;
  };

  // #endregion HELPERS

  // #region HOOKS

  const renderHeaders = (hideCell: { [key: string]: boolean }) => {
    let headers: HeadCell[] = [{ id: 'idx', label: '#', align: 'center', autoWidth: true }];
    fields.forEach((s) =>
      headers.push({
        id: s,
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.${s.charAt(0).toLocaleLowerCase() + s.slice(1)}` }),
        align: 'right',
        hide: hideCell[s],
      })
    );

    headers.push({
      id: 'cageId',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.cageLabel` }),
      align: 'right',
    });

    headers.push({
      id: 'fishGroupName',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.fishGroupLabel` }),
      align: 'right',
    });

    headers.push({ id: 'actions', label: '', align: 'center', autoWidth: true });

    return headers;
  };

  // #endregion HOOKS

  return isError ? (
    <Error error={error} />
  ) : !fieldCollection || (isLoading && cagesIsLoading) ? (
    <LoadingSpinner />
  ) : (
    <>
      <FishCheckTabsHeader
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: fieldCollection.fieldRegistration.name,
                url: String(fieldCollection.fieldRegistration.id),
              },
            ]}
          />
        }
      />

      <Paper transparent>
        <FilterSidebar>
          <Dialog
            maxWidth="sm"
            ref={addFishDialogRef}
            title={intl.formatMessage({ id: `${PAGE_NAME}.header.addNewFishLabel` })}
            button={
              <Button
                fullWidth={isMobile}
                onClick={() => {
                  resetFieldCollection({
                    ...defaultFieldCollection,
                  });
                  setIsValid(true);
                  setTempLength('N/A');
                  setTempWeight('N/A');
                }}>
                <FormattedMessage id={`${PAGE_NAME}.header.addNewFishLabel`} />
              </Button>
            }>
            <form onSubmit={handleSubmitFieldCollection(handleAddFieldCollection)}>
              {addFieldCollection.isPending && <LoadingSpinner overlay />}
              <FlexBox>
                {renderFieldCollectionForm()}
                <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
                  <Button type="submit">
                    <FormattedMessage id="general.nextFish" />
                  </Button>
                </Box>
              </FlexBox>
            </form>
          </Dialog>
        </FilterSidebar>
        <Box mt={8}>
          <FishCheckHeader fishCheck={fieldCollection.fieldRegistration} />
        </Box>
        {Object.keys(welfare).map((dateCreated, idx) => {
          let rows = welfare[dateCreated];
          let cells = hideCell(rows);
          let headers = renderHeaders(cells);

          return (
            <Collapsible key={dateCreated} label={dateCreated} open={idx === 0 ? true : false} count={rows.length} hideBorder>
              <List
                dark
                key={`${dateCreated}_${idx}`}
                options={headers}
                isLoading={isLoading && cagesIsLoading}
                noResult={{
                  show: _.isEmpty(rows),
                  text: <FormattedMessage id="fishCheck.listWelfareScoring.list.noResults" />,
                }}>
                {_.sortBy(rows, (r) => cageMap[r.cageId]).map((row, idx) => renderFishCheckRow(row, cells, idx))}
              </List>
            </Collapsible>
          );
        })}
      </Paper>
    </>
  );
};

export default EditWelfareScoring;
